import { h } from "preact";
import { useEffect, useState } from "preact/hooks";

import EmojisHelpers from "../../../../apps/data/emojis/EmojisHelpers";
import User from "../../../../foundation/api/model/users/User";
import classname from "../../../../foundation/utils/classname";
import { formatList } from "../../../../foundation/utils/legacy";
import { NON_BREAKING_SPACE } from "../../../../foundation/utils/string";
import { getUserDisplayName } from "../../../../foundation/utils/user";
import Tooltip from "../../Tooltip";
import ReactionListLocation from "../ReactionListLocation";

import styles from "./ReactionButton.module.css";

interface Props {
    emoji: string;
    users: User[];
    userId: string;
    location: ReactionListLocation;
    readOnly?: boolean;
    disabled?: boolean;
    onClick(emoji: string): void;
}

function getUserList(users: User[], userId: string) {
    return formatList(users.map(user => {
        const displayName = getUserDisplayName(user).replace(/ /g, NON_BREAKING_SPACE);
        return user._id === userId ? `${displayName}${NON_BREAKING_SPACE}(you)` : displayName;
    })).replace("and ", `and${NON_BREAKING_SPACE}`);
}

const DASHBOARD_TOOLTIP_CONTENT = "In order to add reaction, go to screen";

export default function ReactionButton({
    emoji,
    users,
    userId,
    location,
    readOnly = false,
    disabled = false,
    onClick
}: Props) {
    const [tooltipContent, setTooltipContent] = useState(getUserList(users, userId));

    useEffect(() => {
        setTooltipContent(getUserList(users, userId));
    }, [users, userId]);

    function handleTooltipClose(visible: boolean) {
        if (!visible) {
            setTooltipContent(getUserList(users, userId));
        }
    }

    function handleClick(event: MouseEvent) {
        if (disabled) {
            return;
        }

        if (readOnly) {
            if (location === ReactionListLocation.Dashboard) {
                setTooltipContent(DASHBOARD_TOOLTIP_CONTENT);
            }

            return;
        }

        event.stopPropagation();
        onClick(emoji);
    }

    return (
        <Tooltip
            class={styles.reactionTooltip}
            content={tooltipContent}
            hideDelay={50}
            hideOnClick={false}
            onVisibilityChange={handleTooltipClose}>
            <button
                class={classname(styles.reactionButton, {
                    [styles.userReacted]: users.some(({ _id }) => _id === userId),
                    [styles.readOnly]: readOnly,
                    [styles.disabled]: disabled
                })}
                type="button"
                onClick={handleClick}>
                <span class={styles.reaction}>
                    {EmojisHelpers.searchEmoji(emoji, undefined)!.emoji}
                </span>
                <span class={classname(styles.count, "caption-2", "semibold")}>
                    {users.length}
                </span>
            </button>
        </Tooltip>
    );
}
